import request from "../../utils/request";
import { Result } from "../../models/general";

export enum DocumentTemplateCategory {
  Contract = 'Contract',
  Certificate = 'Certificate',
  Letter = 'Letter',
  Settlement = 'Settlement',
  Other = 'Other',
}

export enum DocumentTemplateStatus {
  Draft = 'Draft',
  Published = 'Published',
  Archived = 'Archived',
}

export interface DocumentTemplateFieldMapping {
  placeholder: string;
  mappedTo?: string;
}

export interface DocumentTemplateView {
  id: number;
  name: string;
  category: DocumentTemplateCategory;
  description?: string;
  originalFileName: string;
  fileSizeBytes: number;
  detectedFields: string[];
  fieldMappings: DocumentTemplateFieldMapping[];
  status: DocumentTemplateStatus;
  publishedOn?: string;
  publishedById?: string;
  timesGenerated: number;
  lastUsedOn?: string;
  createdOn: string;
  createdByName?: string;
}

// Trimmed projection returned to employees browsing published templates.
export interface MyDocumentTemplateView {
  id: number;
  name: string;
  category: DocumentTemplateCategory;
  description?: string;
  originalFileName: string;
  fileSizeBytes: number;
  publishedOn?: string;
}

export interface DocumentDownloadView {
  userName?: string;
  downloadedOn: string;
}

// One selectable field for the mapping dropdown — built-in (label is an i18n key) or custom
// (label/labelAr come from the HR-defined definition). group ∈ Employee | Company | General | Custom.
export interface TemplateAvailableFieldView {
  value: string;
  label: string;
  labelAr?: string;
  group: string;
  isCustom: boolean;
}

export interface DocumentTemplateListView {
  items: DocumentTemplateView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DocumentTemplateListQuery {
  search?: string;
  category?: DocumentTemplateCategory;
  status?: DocumentTemplateStatus;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface CreateDocumentTemplateInput {
  name: string;
  category: DocumentTemplateCategory;
  description?: string;
  detectedFields: string[];
  fieldMappings: DocumentTemplateFieldMapping[];
  // High-fidelity HTML rendered from the source .docx in the wizard (docx-preview). The backend
  // stores only this HTML and renders it to PDF on demand — the .docx is never persisted.
  html: string;
  originalFileName: string;
}

export interface UpdateDocumentTemplateInput {
  id: number;
  name: string;
  category: DocumentTemplateCategory;
  description?: string;
  fieldMappings: DocumentTemplateFieldMapping[];
}

// ── Management (Hr/Admin) ──────────────────────────────────────────────────

export async function getTemplates(query: DocumentTemplateListQuery): Promise<Result<DocumentTemplateListView>> {
  const params = new URLSearchParams();
  if (query.search) params.append("search", query.search);
  if (query.category !== undefined) params.append("category", String(query.category));
  if (query.status !== undefined) params.append("status", String(query.status));
  if (query.sortBy) params.append("sortBy", query.sortBy);
  if (query.sortDescending) params.append("sortDescending", "true");
  if (query.page) params.append("page", String(query.page));
  if (query.pageSize) params.append("pageSize", String(query.pageSize));

  const response = await request.get<Result<DocumentTemplateListView>>(
    `DocumentTemplate/GetTemplates?${params.toString()}`
  );
  return response.data;
}

export async function getTemplate(id: number): Promise<Result<DocumentTemplateView>> {
  const response = await request.get<Result<DocumentTemplateView>>(`DocumentTemplate/GetTemplate/${id}`);
  return response.data;
}

// Downloads the stored template HTML as text (HR-only; used to reload a saved template
// into the wizard for editing/preview).
export async function downloadTemplateHtml(id: number): Promise<string> {
  const response = await request.get(`DocumentTemplate/DownloadTemplate/${id}`, { responseType: "text" });
  return response.data as string;
}

export async function createTemplate(input: CreateDocumentTemplateInput): Promise<Result<DocumentTemplateView>> {
  const response = await request.post<Result<DocumentTemplateView>>("DocumentTemplate/CreateTemplate", input);
  return response.data;
}

export async function updateTemplate(input: UpdateDocumentTemplateInput): Promise<Result<DocumentTemplateView>> {
  const response = await request.put<Result<DocumentTemplateView>>("DocumentTemplate/UpdateTemplate", input);
  return response.data;
}

// Overwrites the stored template HTML (re-rendered from a fresh .docx) and re-detects
// its placeholders server-side.
export async function replaceTemplateHtml(id: number, html: string, originalFileName: string): Promise<Result<DocumentTemplateView>> {
  const response = await request.post<Result<DocumentTemplateView>>(
    `DocumentTemplate/ReplaceTemplateFile/${id}`,
    { html, originalFileName }
  );
  return response.data;
}

export async function duplicateTemplate(id: number): Promise<Result<DocumentTemplateView>> {
  const response = await request.post<Result<DocumentTemplateView>>(`DocumentTemplate/DuplicateTemplate/${id}`);
  return response.data;
}

export async function publishTemplate(id: number): Promise<Result<DocumentTemplateView>> {
  const response = await request.post<Result<DocumentTemplateView>>(`DocumentTemplate/PublishTemplate/${id}`);
  return response.data;
}

export async function unpublishTemplate(id: number): Promise<Result<DocumentTemplateView>> {
  const response = await request.post<Result<DocumentTemplateView>>(`DocumentTemplate/UnpublishTemplate/${id}`);
  return response.data;
}

export async function archiveTemplate(id: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`DocumentTemplate/ArchiveTemplate/${id}`);
  return response.data;
}

export async function restoreTemplate(id: number): Promise<Result<boolean>> {
  const response = await request.post<Result<boolean>>(`DocumentTemplate/RestoreTemplate/${id}`);
  return response.data;
}

export async function deleteTemplate(id: number): Promise<Result<boolean>> {
  const response = await request.delete<Result<boolean>>(`DocumentTemplate/DeleteTemplate/${id}`);
  return response.data;
}

export async function getDownloadHistory(id: number): Promise<Result<DocumentDownloadView[]>> {
  const response = await request.get<Result<DocumentDownloadView[]>>(`DocumentTemplate/DownloadHistory/${id}`);
  return response.data;
}

// The catalog of fields a placeholder can map to: built-in fields + this company's custom fields.
export async function getAvailableFields(): Promise<Result<TemplateAvailableFieldView[]>> {
  const response = await request.get<Result<TemplateAvailableFieldView[]>>("DocumentTemplate/AvailableFields");
  return response.data;
}

// ── Consumption (any authenticated employee, self-scoped) ──────────────────

export async function getMyAvailableTemplates(): Promise<Result<MyDocumentTemplateView[]>> {
  const response = await request.get<Result<MyDocumentTemplateView[]>>("DocumentTemplate/MyAvailableTemplates");
  return response.data;
}

// Fetches the FILLED HTML for the current user (their own data substituted server-side).
// The browser renders this HTML to a PDF client-side — see utils/htmlToPdf.ts — which gives
// correct Arabic shaping/bidi/wrapping that free server-side engines can't do on Windows.
export async function generateMyDocument(id: number): Promise<string> {
  const response = await request.get(`DocumentTemplate/GenerateMyDocument/${id}`, { responseType: "text" });
  return response.data as string;
}
